import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import JSZip from "jszip";
import { buildCompositeSvgString, loadGraphicsData, buildPrintFormats, rasterizeSvgToPng, blobToDataUrl, buildSummarySheet } from "@/lib/exportSvg";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, fetchBlobUrl, formatApiError } from "@/lib/api";
import AdminSettings from "@/pages/AdminSettings";
import { useAuth } from "@/context/AuthContext";
import StudioCanvas from "@/components/studio/StudioCanvas";
import { SecurityDialog } from "@/components/admin/SecurityDialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Loader2, LogOut, Plus, Upload, Trash2, Copy, Eye, Scissors, Package, ClipboardList, Download, Check, Info, FileCode, Pencil, RotateCcw, Archive, ShieldCheck, Ruler, AlertTriangle, SlidersHorizontal, Send, ChevronRight, ChevronDown,
} from "lucide-react";

const EmailStatusBadge = ({ order, formatDate }) => {
  const s = order.email_status;
  if (!s || s === "not_sent") return <span className="text-[#8C867B]" data-testid={`email-status-${order.id}`}>—</span>;
  if (s === "sent")
    return (
      <span title={order.email_sent_at ? `Sent ${formatDate(order.email_sent_at)}` : "Sent"} data-testid={`email-status-${order.id}`}
        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider rounded-full px-2.5 py-1 bg-[#2D6A4F]/15 text-[#2D6A4F]">
        <Check className="h-3 w-3" /> Sent
      </span>
    );
  return (
    <span title={order.email_error || "Delivery failed"} data-testid={`email-status-${order.id}`}
      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider rounded-full px-2.5 py-1 bg-[#C0392B]/15 text-[#C0392B]">
      <AlertTriangle className="h-3 w-3" /> Failed
    </span>
  );
};

const SVG_TEMPLATE = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600">
  <!-- Each sewn part needs an id that STARTS WITH "part-". -->
  <!-- The text after "part-" becomes the label your customer sees. -->
  <rect id="part-front-panel" x="140" y="120" width="300" height="360" rx="10" fill="#E8DFC9" stroke="#B7A888" stroke-width="1.5"/>
  <rect id="part-pocket" x="480" y="210" width="210" height="190" rx="8" fill="#DED3B8" stroke="#B7A888" stroke-width="1.5"/>
  <path id="part-strap" d="M200 120 C200 40 380 40 380 120 L356 120 C356 64 224 64 224 120 Z" fill="#D9CBB2" stroke="#B7A888" stroke-width="1.5"/>
  <!-- A "guide" layer stays on top of the customer's design; its color can be set to Red/Black/White. -->
  <g id="guide-registration">
    <line x1="290" y1="300" x2="290" y2="480" stroke="#111111" stroke-width="2" stroke-dasharray="8 8" fill="none"/>
    <circle cx="290" cy="390" r="10" stroke="#111111" stroke-width="2" fill="none"/>
  </g>
  <!-- CALIBRATION: a square named "measurement" sized to exactly 1x1 inch. Hidden from
       customers and excluded from exports; it unlocks true inch sizing in the studio.
       Size it so its width equals your real 1-inch = N user units (here 72u = 1in). -->
  <rect id="measurement" x="20" y="540" width="72" height="72" fill="#FF00FF"/>
</svg>`;

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logoutAdmin } = useAuth();
  const previewRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newProduct, setNewProduct] = useState({ name: "", description: "" });
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [newOrder, setNewOrder] = useState({ order_number: "", product_id: "", customer_name: "" });
  const [creatingOrder, setCreatingOrder] = useState(false);

  const [viewing, setViewing] = useState(null); // {order, product, graphics}
  const [copiedCode, setCopiedCode] = useState(null);
  const [packaging, setPackaging] = useState(false);
  const [orderFilter, setOrderFilter] = useState("all");
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [resending, setResending] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ pct: 0, label: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, o] = await Promise.all([adminApi.get("/products"), adminApi.get("/orders")]);
      setProducts(p.data);
      setOrders(o.data);
    } catch (err) {
      if (err.response?.status === 401) { logoutAdmin(); navigate("/admin"); }
      else toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [logoutAdmin, navigate]);

  useEffect(() => { load(); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const ready = searchParams.get("ready");
    if (!ready || loading) return;
    (async () => {
      try {
        const { data } = await adminApi.get("/daily-summary/orders", { params: { date: ready } });
        setOrderFilter("submitted");
        setSelectedOrders(new Set(data.ids));
        if (data.ids.length) toast.success(`${data.ids.length} print-ready order${data.ids.length > 1 ? "s" : ""} from ${ready} selected — click the bulk download button`, { duration: 8000 });
        else toast.info(`No print-ready orders found for ${ready}`);
      } catch (e) { toast.error("Could not load the print-ready orders"); }
      setSearchParams({}, { replace: true });
    })();
  }, [searchParams, loading, setSearchParams]);

  const createProduct = async (e) => {
    e.preventDefault();
    setCreatingProduct(true);
    try {
      await adminApi.post("/products", newProduct);
      setNewProduct({ name: "", description: "" });
      toast.success("Product created");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreatingProduct(false); }
  };

  const uploadFramework = async (productId, file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await adminApi.post(`/products/${productId}/framework`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Framework uploaded · ${data.parts.length} parts detected`);
      if (data.calibrated === false) {
        toast.warning('No "measurement" square found — this framework is NOT calibrated. Add a 1×1 inch square named "measurement" so customers see true inch sizes.', { duration: 9000 });
      }
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await adminApi.put(`/products/${editing.id}`, { name: editing.name, description: editing.description });
      toast.success("Product updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const deleteProduct = async (id) => {
    try { await adminApi.delete(`/products/${id}`); toast.success("Product deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const createOrder = async (e) => {
    e.preventDefault();
    if (!newOrder.product_id) { toast.error("Select a product"); return; }
    setCreatingOrder(true);
    try {
      const { data } = await adminApi.post("/orders", newOrder);
      toast.success(`Order created · Access code ${data.access_code}`);
      setNewOrder({ order_number: "", product_id: "", customer_name: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreatingOrder(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 1500);
  };

  const downloadTemplate = () => {
    const blob = new Blob([SVG_TEMPLATE], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "framework-template.svg";
    a.click();
  };

  const viewSubmission = async (orderId) => {
    try {
      const { data } = await adminApi.get(`/orders/${orderId}`);
      const map = {};
      await Promise.all((data.files || []).map(async (f) => {
        try { map[f.id] = await fetchBlobUrl(f.id, "admin_token"); } catch (e) {}
      }));
      const graphics = (data.order?.design?.graphics || []).map((g) => ({ ...g, url: map[g.fileId] }));
      setViewing({ order: data.order, product: data.product, files: data.files || [], blobMap: map, graphics, partColors: data.order?.design?.partColors || {}, guideColor: data.order?.design?.guideColor || "#111111" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadFile = async (fileId, filename) => {
    const url = viewing.blobMap[fileId] || (await fetchBlobUrl(fileId, "admin_token"));
    const a = document.createElement("a");
    a.href = url; a.download = filename || "graphic"; a.click();
  };

  const reopenOrder = async () => {
    try {
      const { data } = await adminApi.post(`/orders/${viewing.order.id}/reopen`);
      setViewing((v) => ({ ...v, order: { ...v.order, status: data.status } }));
      setOrders((list) => list.map((o) => (o.id === data.id ? { ...o, status: data.status } : o)));
      toast.success("Order reopened — customer can edit again");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reopen order");
    }
  };

  const resendEmail = async (id) => {
    setResending(id);
    try {
      const { data } = await adminApi.post(`/orders/${id}/resend-email`);
      setOrders((list) => list.map((o) => (o.id === id ? { ...o, email_status: data.email_status, email_id: data.email_id, email_error: data.email_error, email_sent_at: data.email_sent_at } : o)));
      toast.success("Email resent to customer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resend email");
    } finally {
      setResending(null);
    }
  };

  const fetchAdminFile = (id) => fetchBlobUrl(id, "admin_token");

  const exportComposite = async () => {
    let str;
    try {
      const graphics = await loadGraphicsData(viewing.order?.design?.graphics || [], fetchAdminFile);
      str = buildCompositeSvgString(viewing.product?.framework_svg, graphics, viewing.partColors).str;
    } catch (err) { toast.error("Could not build SVG: " + (err.message || "error")); return; }
    const blob = new Blob([str], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `order-${viewing.order.order_number.replace(/[^\w-]/g, "")}-design.svg`;
    a.click();
  };

  const downloadPrintPackage = async () => {
    setPackaging(true);
    try {
      const graphics = await loadGraphicsData(viewing.order?.design?.graphics || [], fetchAdminFile);
      const flat = buildCompositeSvgString(viewing.product?.framework_svg, graphics, viewing.partColors);
      const { pdf, jpg, width, height } = await buildPrintFormats(flat.str, flat.vw, flat.vh, 4);
      const ord = viewing.order.order_number.replace(/[^\w-]/g, "");
      const zip = new JSZip();
      zip.file(`order-${ord}-print.pdf`, pdf);
      zip.file(`order-${ord}-print.jpg`, jpg);
      zip.file("MANIFEST.txt",
        `Print package\n=============\nOrder: ${viewing.order.order_number}\nProduct: ${viewing.product?.name || ""}\nCustomer: ${viewing.order.customer_name || "-"}\n` +
        `Vector: order-${ord}-print.pdf (${flat.vw} x ${flat.vh}pt, exact SVG dimensions, clean vector lines + embedded art)\n` +
        `Preview: order-${ord}-print.jpg (${width} x ${height}px)\n` +
        `Customer graphics included: ${graphics.length}\n` +
        `Guides excluded. User images clipped to their SVG parts. Import into your RIP software.\n`);
      const content = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(content);
      a.download = `order-${ord}-print-package.zip`;
      a.click();
      toast.success("Print package downloaded");
    } catch (err) {
      toast.error("Could not build print package: " + (err.message || "error"));
    } finally {
      setPackaging(false);
    }
  };

  const isArchived = (o) => !!o.archived;
  const filteredOrders = orders.filter((o) => {
    if (orderFilter === "archived") return isArchived(o);
    if (isArchived(o)) return false;
    if (orderFilter === "all") return true;
    return o.status === orderFilter;
  });
  const allSelected = filteredOrders.length > 0 && filteredOrders.every((o) => selectedOrders.has(o.id));
  const exportableCount = [...selectedOrders].filter((id) => orders.find((o) => o.id === id)?.status === "submitted" && !orders.find((o) => o.id === id)?.archived).length;

  const toggleOrder = (id) => setSelectedOrders((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelectedOrders((s) => {
    const n = new Set(s);
    if (allSelected) filteredOrders.forEach((o) => n.delete(o.id));
    else filteredOrders.forEach((o) => n.add(o.id));
    return n;
  });
  const changeFilter = (k) => { setOrderFilter(k); setSelectedOrders(new Set()); };

  // Group multi-item Shopify orders (#1099A, #1099B...) under a collapsible parent row.
  const shopifyGroups = {};
  filteredOrders.forEach((o) => {
    if (o.source === "shopify" && o.shopify_order_id) {
      (shopifyGroups[o.shopify_order_id] ||= []).push(o);
    }
  });
  const orderUnits = [];
  const seenGroups = new Set();
  filteredOrders.forEach((o) => {
    const gid = o.source === "shopify" && o.shopify_order_id ? o.shopify_order_id : null;
    if (gid && shopifyGroups[gid].length > 1) {
      if (!seenGroups.has(gid)) { seenGroups.add(gid); orderUnits.push({ type: "group", key: gid, orders: shopifyGroups[gid] }); }
    } else {
      orderUnits.push({ type: "single", order: o });
    }
  });
  const toggleGroup = (key) => setExpandedGroups((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleGroupSelect = (orders) => setSelectedOrders((s) => {
    const n = new Set(s);
    const allSel = orders.every((o) => n.has(o.id));
    orders.forEach((o) => (allSel ? n.delete(o.id) : n.add(o.id)));
    return n;
  });

  const orderRow = (o, isChild = false) => (
    <tr key={o.id} className={`border-t border-[#E2DEC9] ${isChild ? "bg-[#FBFAF6]" : ""}`} data-testid={`order-row-${o.id}`}>
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selectedOrders.has(o.id)}
          onChange={() => toggleOrder(o.id)}
          className="accent-[#C85A32] cursor-pointer"
          data-testid={`order-select-${o.id}`}
          title={orderFilter === "archived" ? "Select for bulk delete/restore" : "Select for bulk archive/export"}
        />
      </td>
      <td className={`px-4 py-3 font-medium text-[#1A1918] ${isChild ? "pl-10" : ""}`} data-testid={`order-label-${o.id}`}>
        {o.order_number}
        {o.line_item_title && <span className="block text-xs font-normal text-[#8C867B]">{o.line_item_title}</span>}
      </td>
      <td className="px-4 py-3 text-[#5C5852]">{o.product_name}</td>
      <td className="px-4 py-3 text-[#5C5852]">{o.customer_name || "—"}</td>
      <td className="px-4 py-3">
        <button onClick={() => copyCode(o.access_code)} className="font-mono tracking-widest bg-[#EFECE6] rounded px-2 py-1 hover:bg-[#E2DEC9] flex items-center gap-2" data-testid={`copy-code-${o.id}`}>
          {o.access_code} {copiedCode === o.access_code ? <Check className="h-3 w-3 text-[#2D6A4F]" /> : <Copy className="h-3 w-3 text-[#8C867B]" />}
        </button>
      </td>
      <td className="px-4 py-3">
        <span className={`font-mono text-[10px] uppercase tracking-wider rounded-full px-2.5 py-1 ${o.status === "submitted" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#D4A359]/20 text-[#8a6a25]"}`}>{o.status}</span>
      </td>
      <td className="px-4 py-3">
        <EmailStatusBadge order={o} formatDate={formatDate} />
      </td>
      <td className="px-4 py-3 text-[#5C5852] whitespace-nowrap" data-testid={`order-date-${o.id}`}>{formatDate(o.created_at)}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => viewSubmission(o.id)} className="border-[#C8C2AC]" data-testid={`view-submission-${o.id}`}>
            <Eye className="h-4 w-4 mr-1.5" /> View
          </Button>
          {o.email_status === "failed" && (
            <Button variant="outline" size="sm" onClick={() => resendEmail(o.id)} disabled={resending === o.id} className="border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B]/10" data-testid={`resend-email-${o.id}`} title={o.email_error || "Resend design link email"}>
              {resending === o.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Resend
            </Button>
          )}
          {o.archived ? (
            <Button variant="outline" size="sm" onClick={() => setArchived([o.id], false)} className="border-[#C8C2AC]" data-testid={`restore-order-${o.id}`} title="Restore order">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Restore
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setArchived([o.id], true)} className="border-[#C8C2AC]" data-testid={`archive-order-${o.id}`} title="Archive order">
              <Archive className="h-4 w-4 mr-1.5" /> Archive
            </Button>
          )}
        </div>
      </td>
    </tr>
  );

  const setArchived = async (ids, archived) => {
    if (!ids.length) return;
    try {
      await adminApi.post("/orders/set-archived", { ids, archived });
      setOrders((list) => list.map((o) => (ids.includes(o.id) ? { ...o, archived } : o)));
      setSelectedOrders(new Set());
      toast.success(`${archived ? "Archived" : "Restored"} ${ids.length} order${ids.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error(archived ? "Could not archive" : "Could not restore"); }
  };

  const doBulkDelete = async () => {
    const ids = [...selectedOrders];
    try {
      await adminApi.post("/orders/bulk-delete", { ids });
      setOrders((list) => list.filter((o) => !ids.includes(o.id)));
      setSelectedOrders(new Set());
      toast.success(`Deleted ${ids.length} order${ids.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error("Could not delete orders"); }
    finally { setConfirmDelete(false); }
  };

  const bulkExport = async () => {
    const ids = [...selectedOrders].filter((id) => {
      const o = orders.find((x) => x.id === id);
      return o?.status === "submitted" && !o?.archived;
    });
    if (!ids.length) return;
    setBulkBusy(true);
    setBulkProgress({ pct: 0, label: `Preparing ${ids.length} order${ids.length > 1 ? "s" : ""}…` });
    try {
      const zip = new JSZip();
      let ok = 0;
      const failedOrders = [];
      const summaryItems = [];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        setBulkProgress({ pct: Math.round((i / ids.length) * 85), label: `Generating PDF ${i + 1} of ${ids.length}…` });
        try {
          const { data } = await adminApi.get(`/orders/${id}`);
          const graphics = await loadGraphicsData(data.order?.design?.graphics || [], fetchAdminFile);
          const { str, vw, vh } = buildCompositeSvgString(data.product?.framework_svg, graphics, data.order?.design?.partColors || {});
          const { pdf } = await buildPrintFormats(str, vw, vh, 4);
          const ord = (data.order.order_number || id).replace(/[^\w-]/g, "");
          zip.file(`order-${ord}-print.pdf`, pdf);
          try {
            const thumbBlob = await rasterizeSvgToPng(str, vw, vh, Math.max(1, 1400 / Math.max(vw, vh)));
            summaryItems.push({
              imgDataUrl: await blobToDataUrl(thumbBlob),
              customerName: data.order.customer_name || "—",
              orderNumber: data.order.order_number || "",
              itemLabel: data.order.line_item_title || "",
              productName: data.product?.name || "",
            });
          } catch (e) {}
          ok++;
        } catch (e) {
          const o = orders.find((x) => x.id === id);
          failedOrders.push(`${o?.order_number || id}${o?.line_item_title ? ` (${o.line_item_title})` : ""}: ${e.message || "error"}`);
        }
        setBulkProgress({ pct: Math.round(((i + 1) / ids.length) * 85), label: `Generating PDF ${i + 1} of ${ids.length}…` });
      }
      if (ok === 0) { toast.error("Bulk export failed for all selected orders — " + failedOrders.join("; "), { duration: 15000 }); return; }
      if (failedOrders.length) {
        zip.file("FAILED-ORDERS.txt", `These orders were NOT included because their artwork could not be fully loaded.\nRe-export them individually:\n\n${failedOrders.join("\n")}\n`);
      }
      if (summaryItems.length) {
        setBulkProgress({ pct: 88, label: "Building mirrored summary sheet…" });
        try {
          const { mirroredPdf, mirroredPng, readablePng } = await buildSummarySheet(summaryItems, { title: "Bulk Print Summary" });
          zip.file("SUMMARY-SHEET-MIRRORED.pdf", mirroredPdf);
          zip.file("SUMMARY-SHEET-MIRRORED.png", mirroredPng);
          zip.file("SUMMARY-SHEET-readable.png", readablePng);
        } catch (e) {
          toast.error("Could not build the summary sheet — package downloaded without it");
        }
      }
      setBulkProgress({ pct: 90, label: "Compressing ZIP archive…" });
      const content = await zip.generateAsync({ type: "blob" }, (meta) => {
        setBulkProgress({ pct: 90 + Math.round((meta.percent || 0) * 0.1), label: "Compressing ZIP archive…" });
      });
      setBulkProgress({ pct: 100, label: "Download ready" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(content);
      a.download = `print-packages-${ok}-orders.zip`;
      a.click();
      if (failedOrders.length) {
        toast.error(`Exported ${ok} · ${failedOrders.length} NOT included (artwork failed to load): ${failedOrders.join("; ")}`, { duration: 20000 });
      } else {
        toast.success(`Exported ${ok} print package${ok > 1 ? "s" : ""}`);
      }
      setSelectedOrders(new Set());
    } catch (err) {
      toast.error("Bulk export failed: " + (err.message || "error"));
    } finally {
      setBulkBusy(false);
      setBulkProgress({ pct: 0, label: "" });
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8F6F0]"><Loader2 className="h-6 w-6 animate-spin text-[#C85A32]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F6F0]">
      <header className="flex items-center justify-between px-6 lg:px-12 py-4 bg-[#161616] text-[#F8F6F0]">
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#D4A359]">
          <Scissors className="h-4 w-4" /> Craft Studio · Atelier
        </div>
        <div className="flex items-center gap-5">
          <button onClick={() => setSecurityOpen(true)} data-testid="admin-security-button" className="flex items-center gap-2 text-sm text-[#8C867B] hover:text-[#F8F6F0]">
            <ShieldCheck className="h-4 w-4" /> Security
          </button>
          <button onClick={() => { logoutAdmin(); navigate("/admin"); }} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-[#8C867B] hover:text-[#F8F6F0]">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>
      <SecurityDialog open={securityOpen} onOpenChange={setSecurityOpen} />

      <div className="max-w-6xl mx-auto p-6 lg:p-12">
        <h1 className="font-serif text-4xl text-[#1A1918] mb-1">Atelier Dashboard</h1>
        <p className="text-[#5C5852] mb-8">Manage products, generate access codes and review customer designs.</p>

        <Tabs defaultValue="orders">
          <TabsList className="bg-[#EFECE6] mb-8">
            <TabsTrigger value="products" data-testid="admin-tab-products"><Package className="h-4 w-4 mr-2" /> Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders"><ClipboardList className="h-4 w-4 mr-2" /> Orders & Submissions</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings"><SlidersHorizontal className="h-4 w-4 mr-2" /> Settings</TabsTrigger>
          </TabsList>

          {/* Products */}
          <TabsContent value="products" className="space-y-8">
            <div className="bg-white rounded-lg border border-[#E2DEC9] p-5" data-testid="admin-svg-help">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#C85A32]/10 text-[#C85A32] flex items-center justify-center shrink-0"><Info className="h-4 w-4" /></div>
                <div className="flex-1">
                  <h3 className="font-serif text-lg text-[#1A1918]">How to prepare your SVG framework</h3>
                  <ul className="text-sm text-[#5C5852] mt-2 space-y-1.5 list-disc pl-4">
                    <li>Draw each sewn part as its own shape (rect, path, circle…) or group in your vector tool.</li>
                    <li>Give every part an <span className="font-mono text-[#C85A32]">id</span> that starts with <span className="font-mono text-[#C85A32]">part-</span>. The words after it become the label — e.g. <span className="font-mono">part-front-panel</span> becomes “Front Panel”.</li>
                    <li><b>Illustrator:</b> name each layer/object exactly (e.g. <span className="font-mono">part-strap</span>) — it exports as the id. <b>Inkscape:</b> select the shape → <b>Object&nbsp;Properties</b> → set the <b>ID</b>. Or edit the SVG XML directly.</li>
                    <li>Give each part a base fill; customer graphics and block colors clip neatly inside that shape.</li>
                    <li>Optional: add a <span className="font-mono text-[#C85A32]">guide</span> layer — give any element (or group) an id starting with <span className="font-mono text-[#C85A32]">guide</span>. It always renders on top of the design as a placement guide, and the customer can set it to Red, Black or White.</li>
                    <li><b>Calibration:</b> add a square named <span className="font-mono text-[#C85A32]">measurement</span> (id / label) sized to exactly <b>1×1 inch</b>. It stays hidden from customers and out of exports, and unlocks true inch sizing. Products without it show a <span className="text-[#B0413E] font-medium">Not calibrated</span> flag.</li>
                  </ul>
                  <Button variant="outline" onClick={downloadTemplate} className="mt-4 border-[#C8C2AC]" data-testid="download-svg-template-button"><FileCode className="h-4 w-4 mr-2" /> Download SVG template</Button>
                </div>
              </div>
            </div>

            <form onSubmit={createProduct} className="bg-white rounded-lg border border-[#E2DEC9] p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Product name</Label>
                <Input data-testid="product-name-input" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} required className="border-[#C8C2AC]" placeholder="Custom Tote" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Description</Label>
                <Input data-testid="product-description-input" value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} className="border-[#C8C2AC]" placeholder="Short description" />
              </div>
              <Button data-testid="create-product-button" type="submit" disabled={creatingProduct} className="rounded-full bg-[#C85A32] hover:bg-[#B04B26] h-10">
                {creatingProduct ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> Add product</>}
              </Button>
            </form>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <div key={p.id} className="bg-white rounded-lg border border-[#E2DEC9] overflow-hidden" data-testid={`product-card-${p.id}`}>
                  <div className="aspect-video bg-[#EFECE6] craft-grid flex items-center justify-center overflow-hidden p-3">
                    {p.framework_svg ? (
                      <div className="w-full h-full flex items-center justify-center [&>svg]:max-h-full [&>svg]:max-w-full" dangerouslySetInnerHTML={{ __html: p.framework_svg }} />
                    ) : (
                      <span className="text-xs text-[#8C867B]">No framework uploaded</span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-lg text-[#1A1918]">{p.name}</h3>
                    <p className="text-sm text-[#5C5852] mt-1 line-clamp-2">{p.description}</p>
                    {p.framework_svg && (
                      p.calibrated ? (
                        <span data-testid={`product-calibration-${p.id}`} className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full bg-[#2D6A4F]/12 text-[#2D6A4F] text-[10px] font-mono uppercase tracking-wider">
                          <Ruler className="h-3 w-3" /> Calibrated
                        </span>
                      ) : (
                        <span data-testid={`product-calibration-${p.id}`} className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full bg-[#B0413E]/12 text-[#B0413E] text-[10px] font-mono uppercase tracking-wider" title='Add a 1×1 inch square named "measurement" to enable true inch sizing.'>
                          <AlertTriangle className="h-3 w-3" /> Not calibrated
                        </span>
                      )
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(p.parts || []).map((part) => (
                        <span key={part.id} className="font-mono text-[10px] uppercase tracking-wider bg-[#EFECE6] text-[#5C5852] rounded px-2 py-0.5">{part.label}</span>
                      ))}
                      {(!p.parts || p.parts.length === 0) && <span className="text-[10px] text-[#8C867B]">No parts detected</span>}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <label className="flex-1">
                        <input type="file" accept=".svg,image/svg+xml" hidden onChange={(e) => uploadFramework(p.id, e.target.files?.[0])} data-testid={`upload-framework-input-${p.id}`} />
                        <span className="flex items-center justify-center gap-2 text-sm border border-dashed border-[#C8C2AC] rounded-md h-9 cursor-pointer hover:bg-[#EFECE6]" data-testid="admin-upload-framework-button">
                          <Upload className="h-4 w-4" /> {p.framework_svg ? "Replace SVG" : "Upload SVG"}
                        </span>
                      </label>
                      <Button variant="outline" size="icon" onClick={() => setEditing({ id: p.id, name: p.name, description: p.description || "" })} className="h-9 w-9 border-[#C8C2AC]" data-testid={`edit-product-${p.id}`} title="Edit name & description">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="icon" className="h-9 w-9 border-[#9E2A2B]/40 text-[#9E2A2B] hover:bg-[#9E2A2B]/10" data-testid={`delete-product-${p.id}`} title="Delete product">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#F8F6F0]" data-testid="delete-product-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif text-xl">Delete “{p.name}”?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently removes the product and its uploaded framework. Orders already linked to it will keep their data, but you won't be able to create new orders for it. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="delete-product-cancel">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteProduct(p.id)} className="bg-[#9E2A2B] hover:bg-[#872325] text-white" data-testid="delete-product-confirm">
                              Delete product
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders" className="space-y-8">
            <form onSubmit={createOrder} className="bg-white rounded-lg border border-[#E2DEC9] p-6 grid sm:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Order number</Label>
                <Input data-testid="order-number-input" value={newOrder.order_number} onChange={(e) => setNewOrder({ ...newOrder, order_number: e.target.value })} required className="border-[#C8C2AC]" placeholder="#1024" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Product</Label>
                <select data-testid="order-product-select" value={newOrder.product_id} onChange={(e) => setNewOrder({ ...newOrder, product_id: e.target.value })} required className="w-full h-10 rounded-md border border-[#C8C2AC] bg-white px-3 text-sm">
                  <option value="">Select…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Customer name</Label>
                <Input data-testid="order-customer-input" value={newOrder.customer_name} onChange={(e) => setNewOrder({ ...newOrder, customer_name: e.target.value })} className="border-[#C8C2AC]" placeholder="Optional" />
              </div>
              <Button data-testid="create-order-code-button" type="submit" disabled={creatingOrder} className="rounded-full bg-[#C85A32] hover:bg-[#B04B26] h-10">
                {creatingOrder ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> Generate code</>}
              </Button>
            </form>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2" data-testid="order-filter">
                {[{ k: "all", label: "All" }, { k: "pending", label: "Pending" }, { k: "submitted", label: "Submitted" }, { k: "archived", label: "Archived" }].map((f) => (
                  <button
                    key={f.k}
                    onClick={() => changeFilter(f.k)}
                    data-testid={`order-filter-${f.k}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${orderFilter === f.k ? "bg-[#161616] text-[#F8F6F0] border-[#161616]" : "bg-white border-[#C8C2AC] text-[#5C5852] hover:border-[#161616]"}`}
                  >
                    {f.label} ({
                      f.k === "archived" ? orders.filter((o) => o.archived).length
                        : f.k === "all" ? orders.filter((o) => !o.archived).length
                        : orders.filter((o) => o.status === f.k && !o.archived).length
                    })
                  </button>
                ))}
              </div>
              {selectedOrders.size > 0 && (
                <div className="flex items-center gap-2">
                  {orderFilter === "archived" ? (
                    <>
                      <Button onClick={() => setArchived([...selectedOrders], false)} variant="outline" className="rounded-full border-[#C8C2AC]" data-testid="bulk-restore-button">
                        <RotateCcw className="h-4 w-4 mr-2" /> Restore {selectedOrders.size}
                      </Button>
                      <Button onClick={() => setConfirmDelete(true)} className="rounded-full bg-[#9E2A2B] hover:bg-[#872325] text-white" data-testid="bulk-delete-button">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete {selectedOrders.size}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => setArchived([...selectedOrders], true)} variant="outline" className="rounded-full border-[#C8C2AC]" data-testid="bulk-archive-button">
                        <Archive className="h-4 w-4 mr-2" /> Archive {selectedOrders.size}
                      </Button>
                      {exportableCount > 0 && (
                        <Button onClick={bulkExport} disabled={bulkBusy} className="rounded-full bg-[#C85A32] hover:bg-[#B04B26]" data-testid="bulk-export-button">
                          {bulkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download {exportableCount} print package{exportableCount > 1 ? "s" : ""}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {bulkBusy && (
              <div className="bg-white rounded-lg border border-[#E2DEC9] p-4 mb-4" data-testid="bulk-export-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#5C5852]" data-testid="bulk-export-progress-label">{bulkProgress.label}</span>
                  <span className="font-mono text-sm text-[#C85A32]" data-testid="bulk-export-progress-percent">{bulkProgress.pct}%</span>
                </div>
                <Progress value={bulkProgress.pct} className="bg-[#EFECE6] [&>div]:bg-[#C85A32]" />
              </div>
            )}

            <div className="bg-white rounded-lg border border-[#E2DEC9] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#EFECE6] text-left font-mono text-[10px] uppercase tracking-widest text-[#8C867B]">
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={filteredOrders.length === 0} className="accent-[#C85A32] cursor-pointer" data-testid="order-select-all" title="Select all in view" />
                    </th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Access code</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Date created</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {orderUnits.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#8C867B]">No {orderFilter !== "all" ? orderFilter : ""} orders.</td></tr>}
                  {orderUnits.map((u) => {
                    if (u.type === "single") return orderRow(u.order, false);
                    const g = u.orders;
                    const parent = g[0];
                    const expanded = expandedGroups.has(u.key);
                    const groupSelected = g.every((o) => selectedOrders.has(o.id));
                    const failed = g.filter((o) => o.email_status === "failed").length;
                    const sent = g.filter((o) => o.email_status === "sent").length;
                    return (
                      <Fragment key={u.key}>
                        <tr className="border-t border-[#E2DEC9] bg-[#F3F0E8]" data-testid={`order-group-${u.key}`}>
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={groupSelected} onChange={() => toggleGroupSelect(g)} className="accent-[#C85A32] cursor-pointer" data-testid={`group-select-${u.key}`} title="Select all items in this order" />
                          </td>
                          <td className="px-4 py-3" colSpan={2}>
                            <button onClick={() => toggleGroup(u.key)} className="flex items-center gap-2 font-medium text-[#1A1918] hover:text-[#C85A32]" data-testid={`group-toggle-${u.key}`}>
                              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              <Package className="h-4 w-4 text-[#8C867B]" />
                              {parent.shopify_order_number || parent.order_number}
                              <span className="font-mono text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#E2DEC9] text-[#5C5852]">{g.length} items</span>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-[#5C5852]">{parent.customer_name || "—"}</td>
                          <td className="px-4 py-3 text-[#8C867B]">—</td>
                          <td className="px-4 py-3 text-[#8C867B]">—</td>
                          <td className="px-4 py-3">
                            {failed > 0 ? (
                              <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider rounded-full px-2.5 py-1 bg-[#C0392B]/15 text-[#C0392B]" data-testid={`group-email-${u.key}`}><AlertTriangle className="h-3 w-3" /> {failed} failed</span>
                            ) : sent === g.length && sent > 0 ? (
                              <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider rounded-full px-2.5 py-1 bg-[#2D6A4F]/15 text-[#2D6A4F]" data-testid={`group-email-${u.key}`}><Check className="h-3 w-3" /> Sent</span>
                            ) : (
                              <span className="text-[#8C867B]" data-testid={`group-email-${u.key}`}>—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-[#5C5852] whitespace-nowrap">{formatDate(parent.created_at)}</td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="outline" size="sm" onClick={() => toggleGroup(u.key)} className="border-[#C8C2AC]" data-testid={`group-expand-btn-${u.key}`}>
                              {expanded ? "Collapse" : "Expand"}
                            </Button>
                          </td>
                        </tr>
                        {expanded && g.map((o) => orderRow(o, true))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            <AdminSettings />
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-[#F8F6F0]" data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">Delete {selectedOrders.size} archived order{selectedOrders.size > 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected archived order{selectedOrders.size > 1 ? "s" : ""} and their uploaded files. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doBulkDelete} className="bg-[#9E2A2B] hover:bg-[#872325] text-white" data-testid="bulk-delete-confirm">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submission viewer */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl bg-[#F8F6F0]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Order {viewing?.order?.order_number} · {viewing?.product?.name}</DialogTitle>
            <DialogDescription className="text-[#5C5852]">Review the customer's placed graphics and download production files.</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="grid md:grid-cols-[1.2fr_1fr] gap-6">
              <div>
                <div ref={previewRef} className="aspect-square bg-white rounded-md border border-[#E2DEC9] overflow-hidden" data-testid="admin-submission-preview">
                  <StudioCanvas svgString={viewing.product?.framework_svg} graphics={viewing.graphics} partColors={viewing.partColors} guideColor={viewing.guideColor} onChange={() => {}} editable={false} />
                </div>
                <Button onClick={downloadPrintPackage} disabled={packaging} className="w-full mt-3 rounded-full bg-[#C85A32] hover:bg-[#B04B26]" data-testid="admin-download-print-package-button">
                  {packaging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download Print Package
                </Button>
                <Button onClick={exportComposite} variant="outline" className="w-full mt-2 border-[#C8C2AC]" data-testid="admin-submission-download-svg-button">
                  <Download className="h-4 w-4 mr-2" /> Composite SVG only
                </Button>
                {viewing.order.status !== "pending" && (
                  <Button onClick={reopenOrder} variant="outline" className="w-full mt-2 border-[#9E2A2B]/40 text-[#9E2A2B] hover:bg-[#9E2A2B]/10" data-testid="admin-reopen-order-button">
                    <RotateCcw className="h-4 w-4 mr-2" /> Reopen Order for Edits
                  </Button>
                )}
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867B] mb-3">Uploaded graphics ({viewing.files.length})</p>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {viewing.files.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 bg-white rounded-md border border-[#E2DEC9] p-2">
                      <div className="w-12 h-12 bg-[#EFECE6] rounded overflow-hidden shrink-0">
                        {viewing.blobMap[f.id] && <img src={viewing.blobMap[f.id]} alt={f.original_filename} className="w-full h-full object-contain" />}
                      </div>
                      <span className="flex-1 text-sm text-[#5C5852] truncate">{f.original_filename}</span>
                      <Button variant="ghost" size="icon" onClick={() => downloadFile(f.id, f.original_filename)} className="h-8 w-8" data-testid={`download-file-${f.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {viewing.files.length === 0 && <p className="text-sm text-[#8C867B]">No graphics uploaded by the customer.</p>}
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867B] mt-6 mb-2">Placements</p>
                <p className="text-sm text-[#5C5852]">{viewing.graphics.length} graphic(s) placed across {new Set(viewing.graphics.map((g) => g.partId)).size} part(s).</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit product */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#F8F6F0]" data-testid="edit-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit product</DialogTitle>
            <DialogDescription className="text-[#5C5852]">Update the name and description shown in the studio.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Product name</Label>
                <Input data-testid="edit-product-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required className="border-[#C8C2AC] bg-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Description</Label>
                <Textarea data-testid="edit-product-description-input" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={4} className="border-[#C8C2AC] bg-white" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="border-[#C8C2AC]" data-testid="edit-product-cancel">Cancel</Button>
                <Button type="submit" disabled={savingEdit} className="rounded-full bg-[#C85A32] hover:bg-[#B04B26]" data-testid="edit-product-save">
                  {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
